import { useMutation, useQuery } from '@apollo/client';
import { Buffer } from 'buffer';
import gql from 'graphql-tag';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Alert, AlertVariant } from '../../components/alert/alert';
import { Button } from '../../components/button/button';
import { Container } from '../../components/container/container';
import { FieldGroup } from '../../components/field-group/field-group';
import { Heading } from '../../components/heading/heading';
import { Input } from '../../components/input/input';
import {
  DropDownIcon,
  SelectBox,
} from '../../components/register-screens/select-account';
import {
  Auction,
  ImportLotsDocument,
  useActiveAuctionQuery,
  useAllAuctionsQuery,
  useCreateOrUpdateAuctionMutation,
  useImportLotImagesMutation,
  useMeQuery,
  useStaggerLotEndingsMutation,
  useUpdateAuctionDetailsMutation,
  useUpdateMinimumBidAndIncrementMutation,
} from '../../graphql/types';
import { color } from '../../styles/mixins';
import { label12, typography } from '../../styles/typography';
import { client } from '../../utils/apollo-client';
import dayjs, { PDTTimeZone } from '../../utils/dayjs';

const Center = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  ${typography.fonts.mercury(32)}
  color: ${color('gray80')};
`;

const Label = styled.label`
  display: block;
  ${label12};
  line-height: 18px;
  color: ${color('gray80')};
`;

const StyledSelectBox = styled(SelectBox)`
  && {
    border: 1px solid ${color('gray50')};

    select {
      padding-right: 40px;
    }

    svg {
      right: 20px;
    }
  }
`;

function StaggerLotEndings({ activeAuctionId }: { activeAuctionId?: string }) {
  const [auctionId, setAuctionId] = useState(activeAuctionId ?? '');
  const [staggerLotEndingsMutation, { loading }] =
    useStaggerLotEndingsMutation();
  const [intervalCount, setIntervalCount] = useState(8);
  const [intervalSeconds, setIntervalSeconds] = useState(120);

  const { data: auctions } = useAllAuctionsQuery();

  useEffect(() => {
    if (activeAuctionId) {
      setAuctionId(activeAuctionId);
    }
  }, [activeAuctionId]);

  return (
    <FieldGroup title="Stagger Lot endings">
      <Alert copy="This will stagger all lot end times by given parameters" />

      <div>
        <Label htmlFor="auction">Auction:</Label>
        <StyledSelectBox>
          <select
            name="auctionId"
            id="auctionId"
            onChange={(e) => {
              setAuctionId(e.target.value);
              setIntervalCount(8);
              setIntervalSeconds(120);
            }}
            value={auctionId}
          >
            {auctions?.allAuctions?.edges
              .filter(({ node }) => node.active)
              .map(({ node: auction }) => (
                <option key={auction.id} value={auction.id}>
                  {auction.title} (
                  {dayjs(auction.startsAt)
                    .tz(PDTTimeZone)
                    .format('YYYY-MM-DD HH:mm:ss z')}
                  )
                </option>
              ))}
          </select>
          <DropDownIcon />
        </StyledSelectBox>
      </div>

      <label htmlFor="input1">
        Lots per interval:
        <Input
          id="input1"
          type="number"
          value={String(intervalCount)}
          onChange={(e) => setIntervalCount(Number(e.currentTarget.value))}
        />
      </label>
      <label htmlFor="input2">
        Seconds between intervals:
        <Input
          id="input2"
          type="number"
          value={String(intervalSeconds)}
          onChange={(e) => setIntervalSeconds(Number(e.currentTarget.value))}
        />
      </label>
      <Button
        style={{ marginTop: 16 }}
        disabled={loading}
        onClick={() => {
          staggerLotEndingsMutation({
            variables: {
              auctionId: auctionId || '',
              intervalCount,
              intervalSeconds,
            },
          });
        }}
      >
        Submit
      </Button>
    </FieldGroup>
  );
}

function ImportLots() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [mutationState, setMutationState] = useState<
    'success' | 'error' | undefined
  >(undefined);

  return (
    <FieldGroup title="Import lots">
      {!mutationState && (
        <Alert copy="Import lots as CSV file. This will update the current active auction's lots if it exists, and only if that auction is for the current year. Lot import is an incremental import. If you need to change the lot numbering or delete lots, please create a new auction." />
      )}
      {mutationState === 'success' && (
        <Alert
          variant={AlertVariant.Success}
          copy="Lots successfully imported."
        />
      )}
      {mutationState === 'error' && (
        <Alert
          variant={AlertVariant.Danger}
          copy={`${errorMessage ?? 'Something went wrong.'}`}
        />
      )}
      <label htmlFor="input3">
        CSV file:
        <Input type="file" id="csv_file" accept=".csv" />
      </label>
      <Button
        style={{ marginTop: 16 }}
        onClick={() => {
          const csvFile = document.getElementById(
            'csv_file'
          ) as HTMLInputElement;
          setMutationState(undefined);
          const file = csvFile?.files?.[0];
          if (file) {
            file.text().then((text) => {
              client
                .mutate({
                  mutation: ImportLotsDocument,
                  variables: {
                    base64csv: Buffer.from(text, 'binary').toString('base64'),
                  },
                })
                .then(({ data }) => {
                  if (data.importLots) {
                    setMutationState('success');
                    csvFile.value = '';
                  } else {
                    setMutationState('error');
                  }
                })
                .catch((e) => {
                  console.error(e);
                  setMutationState('error');
                  setErrorMessage(e);
                });
            });
          } else {
            setMutationState('error');
            setErrorMessage('No file selected.');
          }
        }}
      >
        Submit
      </Button>
    </FieldGroup>
  );
}

function ButtonCreate2022Auction({ disabled }: { disabled: boolean }) {
  const [create2022Auction, { data, loading }] = useMutation(gql`
    mutation {
      create2022AuctionAndLots
    }
  `);
  return (
    <div>
      <Button
        style={{ marginBottom: 16 }}
        onClick={() => create2022Auction()}
        loading={loading}
        disabled={disabled || data?.create2022AuctionAndLots}
      >
        Create 2022 Auction
      </Button>
    </div>
  );
}

function ButtonCreate2023Auction({ disabled }: { disabled: boolean }) {
  const [create2023Auction, { data, loading }] = useMutation(gql`
    mutation {
      create2023AuctionAndLots
    }
  `);
  return (
    <div>
      <Button
        style={{ marginBottom: 16 }}
        onClick={() => create2023Auction()}
        loading={loading}
        disabled={disabled || data?.create2023AuctionAndLots}
      >
        Create 2023 Auction
      </Button>
    </div>
  );
}

export function CreatePresetAuctions() {
  const { data, loading } = useQuery(gql`
    {
      getAllTheAuctionTitles
    }
  `);

  const titles = (data?.getAllTheAuctionTitles as string[]) ?? [];
  const has2022Auction = titles.includes('Napa Auction 2022');
  const has2023Auction = titles.includes('Napa Auction 2023');

  return (
    <FieldGroup title="Import 2022 and 2023 auction and lots">
      {loading ? (
        'Loading stats...'
      ) : data ? (
        <>
          <ButtonCreate2022Auction disabled={has2022Auction} />
          <ButtonCreate2023Auction disabled={has2023Auction} />
        </>
      ) : (
        <>No data</>
      )}
    </FieldGroup>
  );
}

function CreateOrUpdateAuction({
  auction,
}: {
  auction?: Partial<Auction> | null;
}) {
  const [createOrUpdateAuctionMutation, { loading }] =
    useCreateOrUpdateAuctionMutation({
      refetchQueries: 'active',
    });
  const { data: auctions } = useAllAuctionsQuery();

  const [auctionId, setAuctionId] = useState(auction?.id || '');
  const [buyersPremiumPercentage, setBuyersPremiumPercentage] = useState(
    auction?.buyersPremiumPercentage === undefined
      ? 7
      : auction.buyersPremiumPercentage
  );
  const [overheadPremiumPercentage, setOverheadPremiumPercentage] = useState(
    auction?.overheadPremiumPercentage === undefined
      ? 1
      : auction.overheadPremiumPercentage
  );
  const [title, setTitle] = useState(auction?.title || '');
  const [startsAt, setStartsAt] = useState(
    dayjs(auction?.startsAt).tz(PDTTimeZone).format('YYYY-MM-DDTHH:mm')
  );
  const [endsAt, setEndsAt] = useState(
    dayjs(auction?.endsAt).tz(PDTTimeZone).format('YYYY-MM-DDTHH:mm')
  );
  const [active, setActive] = useState(auction?.active ?? true);

  useEffect(() => {
    if (auction?.id) {
      setAuctionId(auction.id);
    }
  }, [auction?.id]);

  return (
    <FieldGroup title="Create or update auction">
      <div>
        <Label htmlFor="auction">Auction:</Label>
        <StyledSelectBox>
          <select
            name="auctionId"
            id="auctionId"
            onChange={(e) => {
              const auction = auctions?.allAuctions?.edges.find(
                ({ node }) => node.id === e.target.value
              )?.node;

              setAuctionId(e.target.value);
              setTitle(auction?.title || '');
              setStartsAt(
                dayjs(auction?.startsAt)
                  .tz(PDTTimeZone)
                  .format('YYYY-MM-DDTHH:mm')
              );
              setEndsAt(
                dayjs(auction?.endsAt)
                  .tz(PDTTimeZone)
                  .format('YYYY-MM-DDTHH:mm')
              );
              setActive(auction?.active ?? true);
              setBuyersPremiumPercentage(
                auction?.buyersPremiumPercentage === undefined
                  ? 7
                  : auction.buyersPremiumPercentage
              );
              setOverheadPremiumPercentage(
                auction?.overheadPremiumPercentage === undefined
                  ? 1
                  : auction.overheadPremiumPercentage
              );
            }}
            value={auctionId}
          >
            <option value="">Create new</option>
            {auctions?.allAuctions?.edges.map(({ node: auction }) => (
              <option key={auction.id} value={auction.id}>
                {auction.title} (
                {dayjs(auction.startsAt)
                  .tz(PDTTimeZone)
                  .format('YYYY-MM-DD HH:mm:ss z')}
                )
              </option>
            ))}
          </select>
          <DropDownIcon />
        </StyledSelectBox>
      </div>
      <div>
        <Label htmlFor="auction_title">Title:</Label>
        <Input
          placeholder="Title"
          id="auction_title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
      </div>
      <div>
        <Label htmlFor="auction_start">Starts at:</Label>
        <Input
          placeholder="Starts At"
          id="auction_start"
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.currentTarget.value)}
        />
      </div>
      <div>
        <Label htmlFor="auction_end">Ends at:</Label>
        <Input
          placeholder="Ends at"
          id="auction_end"
          type="datetime-local"
          value={endsAt}
          onChange={(e) => setEndsAt(e.currentTarget.value)}
        />
      </div>
      <div>
        <Label htmlFor="buyers_premium">Buyer&apos;s premium percentage:</Label>
        <Input
          type="number"
          min="0"
          max="100"
          placeholder="Buyer's premium %"
          id="buyers_premium"
          value={buyersPremiumPercentage}
          onChange={(e) =>
            setBuyersPremiumPercentage(parseInt(e.currentTarget.value))
          }
        />
      </div>
      <div>
        <Label htmlFor="overhead_premium">Overhead premium percentage:</Label>
        <Input
          type="number"
          min="0"
          max="100"
          placeholder="Overhead premium %"
          id="overhead_premium"
          value={overheadPremiumPercentage}
          onChange={(e) =>
            setOverheadPremiumPercentage(parseInt(e.currentTarget.value))
          }
        />
      </div>
      <div>
        <Label htmlFor="auction_active">Active:</Label>
        <input
          type="checkbox"
          id="auction_active"
          style={{ appearance: 'auto' }}
          checked={active}
          onChange={(e) => setActive(e.currentTarget.checked)}
        />
      </div>
      <br />
      <br />
      <Button
        style={{ marginBottom: 16 }}
        disabled={loading}
        onClick={() => {
          createOrUpdateAuctionMutation({
            variables: {
              auctionId,
              active,
              endsAt: dayjs.tz(endsAt, PDTTimeZone).toISOString(),
              startsAt: dayjs.tz(startsAt, PDTTimeZone).toISOString(),
              title,
              buyersPremiumPercentage,
              overheadPremiumPercentage,
            },
          });
        }}
      >
        {auctionId !== '' ? 'Update' : 'Create'} auction
      </Button>
    </FieldGroup>
  );
}

function UpdateStartBid({ activeAuctionId }: { activeAuctionId?: string }) {
  const [auctionId, setAuctionId] = useState(activeAuctionId);
  const [minimumBidAmount, setMinimumBidAmount] = useState(0);
  const [incrementAmount, setIncrementAmount] = useState(0);
  const [minimumIncrement, setMinimumIncrement] = useState(0);
  const [maximumIncrement, setMaximumIncrement] = useState(0);

  const [updateMinimumBidAmount] = useUpdateMinimumBidAndIncrementMutation();
  const { data: auctions, refetch } = useAllAuctionsQuery();

  useEffect(() => {
    if (activeAuctionId) {
      setAuctionId(activeAuctionId);
    }
  }, [activeAuctionId]);

  useEffect(() => {
    if (!auctionId) {
      return;
    }

    const auction = auctions?.allAuctions?.edges.find(
      ({ node }) => node.id === auctionId
    )?.node;

    if (auction?.lots[0]?.minimumBidAmount) {
      setMinimumBidAmount(auction.lots[0].minimumBidAmount);
    }

    if (auction?.lots[0]?.incrementAmount) {
      setIncrementAmount(auction.lots[0].incrementAmount);
    }

    if (auction?.lots[0]?.minimumIncrement) {
      setMinimumIncrement(auction.lots[0].minimumIncrement);
    }

    if (auction?.lots[0]?.maximumIncrement) {
      setMaximumIncrement(auction.lots[0].maximumIncrement);
    }
  }, [auctions, auctionId]);

  return (
    <FieldGroup title="Update start bid and increment">
      <div>
        <Label htmlFor="auction">Auction:</Label>
        <StyledSelectBox>
          <select
            name="auctionId"
            id="auctionId"
            onChange={(e) => {
              const auction = auctions?.allAuctions?.edges.find(
                ({ node }) => node.id === e.target.value
              )?.node;

              setAuctionId(e.target.value);
              setMinimumBidAmount(auction?.lots[0]?.minimumBidAmount ?? 0);
            }}
            value={auctionId}
          >
            <option value="">Create new</option>
            {auctions?.allAuctions?.edges
              .filter(({ node }) => node.active)
              .map(({ node: auction }) => (
                <option key={auction.id} value={auction.id}>
                  {auction.title} (
                  {dayjs(auction.startsAt)
                    .tz(PDTTimeZone)
                    .format('YYYY-MM-DD HH:mm:ss z')}
                  )
                </option>
              ))}
          </select>
          <DropDownIcon />
        </StyledSelectBox>
      </div>
      <div>
        <Label htmlFor="auction_title">Minimum Bid Amount:</Label>
        <Input
          type="number"
          placeholder="Minimum Bid Amount"
          id="min_bid"
          value={minimumBidAmount}
          onChange={(e) => {
            const value = parseInt(e.currentTarget.value, 10);
            setMinimumBidAmount(isNaN(value) ? 0 : value);
          }}
        />
      </div>
      <div>
        <Label htmlFor="increment">Increment Amount:</Label>
        <Input
          type="number"
          title="Increment Amount"
          placeholder="Increment Amount"
          id="increment"
          value={incrementAmount}
          onChange={(e) => {
            const value = parseInt(e.currentTarget.value, 10);
            setIncrementAmount(isNaN(value) ? 0 : value);
          }}
        />
      </div>

      <div>
        <Label htmlFor="minimumIncrement">Minimum Increment:</Label>
        <Input
          type="number"
          title="Minimum Increment"
          placeholder="Minimum Increment"
          id="minimumIncrement"
          value={minimumIncrement}
          onChange={(e) => {
            const value = parseInt(e.currentTarget.value, 10);
            setMinimumIncrement(isNaN(value) ? 0 : value);
          }}
        />
      </div>
      <div>
        <Label htmlFor="maximumIncrement">Maximum Increment:</Label>
        <Input
          type="number"
          title="Maximum Increment"
          placeholder="Maximum Increment"
          id="maximumIncrement"
          value={maximumIncrement}
          onChange={(e) => {
            const value = parseInt(e.currentTarget.value, 10);
            setMaximumIncrement(isNaN(value) ? 0 : value);
          }}
        />
      </div>

      <br />
      <Button
        style={{ marginBottom: 16 }}
        onClick={() => {
          if (!auctionId) {
            return;
          }

          updateMinimumBidAmount({
            variables: {
              auctionId,
              minimumBidAmount,
              incrementAmount,
              minimumIncrement,
              maximumIncrement,
            },
          });
          refetch();
        }}
      >
        Submit
      </Button>
    </FieldGroup>
  );
}

export function Update2023AuctionFromCsv() {
  const [update2023Auction, { loading }] = useMutation(gql`
    mutation {
      update2023AuctionAndLots
    }
  `);
  return (
    <FieldGroup title="Update 2023 Auction with data from CSV">
      <Button
        style={{ marginBottom: 16 }}
        onClick={() => update2023Auction()}
        loading={loading}
      >
        Update 2023 Auction
      </Button>
    </FieldGroup>
  );
}

export function UploadLotImages() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [mutationState, setMutationState] = useState<
    'success' | 'error' | undefined
  >(undefined);

  const [importLotImages, { loading }] = useImportLotImagesMutation();
  const ref = useRef<HTMLInputElement>(null);

  return (
    <FieldGroup title="Import lot images">
      {!mutationState && (
        <Alert copy="Import lot images. Make sure the name of the image is same with the lot number." />
      )}
      {mutationState === 'success' && (
        <Alert
          variant={AlertVariant.Success}
          copy="Lot images successfully imported."
        />
      )}
      {mutationState === 'error' && (
        <Alert
          variant={AlertVariant.Danger}
          copy={`${errorMessage ?? 'Something went wrong.'}`}
        />
      )}

      <Input ref={ref} type="file" accept="image/jpg" multiple />
      <Button
        style={{ marginTop: 16 }}
        loading={loading}
        onClick={async () => {
          setMutationState(undefined);
          try {
            const { data } = await importLotImages({
              variables: { images: ref.current?.files },
            });

            if (data?.importLotImages) {
              setMutationState('success');
            } else {
              setMutationState('error');
            }
          } catch (e) {
            console.error(e);
            setMutationState('error');
            setErrorMessage(e as string);
          }
        }}
      >
        Submit
      </Button>
    </FieldGroup>
  );
}

export function UpdateAuctionDetails({
  activeAuctionId,
}: {
  activeAuctionId?: string;
}) {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [mutationState, setMutationState] = useState<
    'success' | 'error' | undefined
  >(undefined);

  const [auctionId, setAuctionId] = useState(activeAuctionId ?? '');
  const [faqLink, setFaqLink] = useState('');
  const [description, setDescription] = useState('');
  const [youtubeId, setYoutubeId] = useState('');

  const heroImageRef = useRef<HTMLInputElement>(null);
  const heroImageMobileRef = useRef<HTMLInputElement>(null);
  const heroImageMobile2xRef = useRef<HTMLInputElement>(null);

  const { data: auctions, refetch } = useAllAuctionsQuery();

  const [updateAuctionDetailsMutation, { loading }] =
    useUpdateAuctionDetailsMutation();

  useEffect(() => {
    if (activeAuctionId) {
      setAuctionId(activeAuctionId);
    }
  }, [activeAuctionId]);

  useEffect(() => {
    if (!auctionId) {
      return;
    }

    const auction = auctions?.allAuctions?.edges.find(
      ({ node }) => node.id === auctionId
    )?.node;

    if (auction?.faqLink) {
      setFaqLink(auction.faqLink);
    }

    if (auction?.description) {
      setDescription(auction.description);
    }

    if (auction?.youtubeId) {
      setYoutubeId(auction.youtubeId);
    }
  }, [auctionId, auctions]);

  return (
    <FieldGroup title="Update auction details">
      {mutationState === 'success' && (
        <Alert
          variant={AlertVariant.Success}
          copy={`Auction successfully ${
            auctionId !== '' ? 'updated' : 'created'
          }.`}
        />
      )}
      {mutationState === 'error' && (
        <Alert
          variant={AlertVariant.Danger}
          copy={`${errorMessage ?? 'Something went wrong.'}`}
        />
      )}

      <div>
        <Label htmlFor="auction">Auction:</Label>
        <StyledSelectBox>
          <select
            name="auctionId"
            id="auctionId"
            onChange={(e) => {
              setAuctionId(e.target.value);
            }}
            value={auctionId}
          >
            {auctions?.allAuctions?.edges
              .filter(({ node }) => node.active)
              .map(({ node: auction }) => (
                <option key={auction.id} value={auction.id}>
                  {auction.title} (
                  {dayjs(auction.startsAt)
                    .tz(PDTTimeZone)
                    .format('YYYY-MM-DD HH:mm:ss z')}
                  )
                </option>
              ))}
          </select>
          <DropDownIcon />
        </StyledSelectBox>
      </div>

      <div>
        <Label htmlFor="faqLink">FAQ link:</Label>
        <Input
          placeholder="FAQ Link"
          id="faqLink"
          value={faqLink}
          onChange={(e) => setFaqLink(e.currentTarget.value)}
        />
      </div>
      <div>
        <Label htmlFor="description">Description:</Label>
        <ReactQuill
          theme="snow"
          modules={{ toolbar: ['bold', 'italic', 'link', 'clean'] }}
          placeholder="Description"
          id="description"
          value={description}
          onChange={setDescription}
        />
      </div>
      <div>
        <Label htmlFor="youtubeId">Youtube Id:</Label>
        <Input
          placeholder="Youtube Id"
          id="youtubeId"
          value={youtubeId}
          onChange={(e) => setYoutubeId(e.currentTarget.value)}
        />
      </div>
      <div>
        <Label htmlFor="heroImage">
          Hero Image: (Suggested dimensions: 1920 x 1080)
        </Label>
        <Input id="heroImage" ref={heroImageRef} type="file" accept="image/*" />
      </div>
      <div>
        <Label htmlFor="heroImageMobile">
          Hero Image Mobile: (Suggested dimensions: 405 x 720)
        </Label>
        <Input
          id="heroImageMobile"
          ref={heroImageMobileRef}
          type="file"
          accept="image/*"
        />
      </div>
      <div>
        <Label htmlFor="heroImageMobile2x">
          Hero Image Mobile 2x: (Suggested dimensions: 608 x 1080)
        </Label>
        <Input
          id="heroImageMobile"
          ref={heroImageMobile2xRef}
          type="file"
          accept="image/*"
        />
      </div>
      <Button
        style={{ marginTop: 16 }}
        disabled={loading}
        onClick={async () => {
          setMutationState(undefined);
          try {
            const { data } = await updateAuctionDetailsMutation({
              variables: {
                auctionId: auctionId || '',
                faqLink,
                description: `${description}<br>`, // manually add <br> to make sure we have a spacing with the FAQ button in index page
                youtubeId,
                heroImage: heroImageRef.current?.files?.item(0),
                heroImageMobile: heroImageMobileRef.current?.files?.item(0),
                heroImageMobile2x: heroImageMobile2xRef.current?.files?.item(0),
              },
            });

            if (data?.updateAuctionDetails.id) {
              setMutationState('success');
              refetch();
            } else {
              setMutationState('error');
            }
          } catch (e) {
            console.error(e);
            setMutationState('error');
            setErrorMessage(e as string);
          }
        }}
      >
        Submit
      </Button>
    </FieldGroup>
  );
}

export function AdminDebug() {
  const me = useMeQuery({
    fetchPolicy: 'cache-only',
  });
  const { data: activeAuction } = useActiveAuctionQuery();

  if (me.loading || !me.data) {
    return <Center>Loading...</Center>;
  }

  if (me.data && !me.data?.me?.isAdmin) {
    return <Center>No admin</Center>;
  }

  return (
    <Container>
      <div style={{ maxWidth: 800, width: '100%' }}>
        <Heading style={{ marginBottom: 20 }}>Admin system</Heading>
        <CreateOrUpdateAuction auction={activeAuction?.activeAuction} />
        <UpdateStartBid activeAuctionId={activeAuction?.activeAuction?.id} />
        <StaggerLotEndings activeAuctionId={activeAuction?.activeAuction?.id} />
        <UpdateAuctionDetails
          activeAuctionId={activeAuction?.activeAuction?.id}
        />
        <ImportLots />
        <UploadLotImages />
        {/* <CreatePresetAuctions />
        <Update2023AuctionFromCsv /> */}
      </div>
    </Container>
  );
}
